import * as Dialog from "@radix-ui/react-dialog";
import { ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { MdClose } from "react-icons/md";
import { LogInForm } from "views/auth/scheme/authForm";
import { adminsService } from "views/services/adminsService";
import { AxiosError } from "axios";
import InputField from "components/fields/InputField";
import { toast } from "App";

interface CampaignDialogProps {
  children: ReactNode;
}

export const AdminDialog = ({ children }: CampaignDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<LogInForm>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  if (!form) {
    throw new Error("useFormContext must be used inside a FormProvider");
  }

  const handleOpenChange = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const handleSubmit = async (data: LogInForm) => {
    try {
      await adminsService.createAdmin(data.email, data.password);
      await adminsService.getAdminsList();
      handleOpenChange(false);
      toast({
        title: "Success",
        description: 'New admin successfully created',
        status: "success",
        position: "top-right",
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        form.setError("root", {
          type: "custom",
          message: error.response?.data.message || "Something went wrong",
        });

        if (error.response?.data.message === "Invalid credentials") {
          form.setError("email", {
            type: "custom",
          });
          form.setError("password", {
            type: "custom",
          });
        }
      }
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal>
        <Dialog.Trigger asChild>{children}</Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-50 bg-gray-800 bg-opacity-50" />
          <Dialog.Content
            onInteractOutside={(e) => {
              if ((e.target as HTMLElement).closest(".chakra-toast")) {
                e.preventDefault();
              }
            }}
            className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[60] max-h-[85vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none"
          >
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <Dialog.Title className="text-mauve12 mb-10 text-[17px] font-medium">
                Create Admin
              </Dialog.Title>
              <div className="max-h-[55dvh] grow overflow-y-auto overflow-x-hidden px-px pr-4">
                <div className="w-full max-w-full flex-col items-center xl:max-w-[420px]">
                  {/* Email */}
                  <fieldset className="mb-[15px] flex flex-col">
                    <InputField
                      variant="auth"
                      state={form.formState.errors.email ? "error" : undefined}
                      extra="mb-3"
                      label="Login*"
                      placeholder=""
                      id="email"
                      type="text"
                      autoComplete="username"
                      {...form.register("email", {
                        required: "This field is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Invalid email address",
                        },
                        onChange: () => {
                          form.clearErrors("root");
                          form.clearErrors("email");
                        },
                      })}
                    />
                    {form.formState.errors.email && (
                      <span className="text-sm text-red-500">
                        {form.formState.errors.email.message}
                      </span>
                    )}
                  </fieldset>

                  {/* Password */}

                  <fieldset className="mb-[15px] flex flex-col">
                    <InputField
                      variant="auth"
                      state={
                        form.formState.errors.password ? "error" : undefined
                      }
                      extra="mb-3"
                      label="Password*"
                      placeholder="Min. 8 characters"
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      {...form.register("password", {
                        required: "This field is required",
                        minLength: {
                          value: 8,
                          message: "Password must be at least 8 characters",
                        },
                        onChange: () => {
                          form.clearErrors("root");
                          form.clearErrors("password");
                        },
                      })}
                    />
                    {form.formState.errors.password && (
                      <span className="text-sm text-red-500">
                        {form.formState.errors.password.message}
                      </span>
                    )}
                  </fieldset>                  
                </div>
              </div>
              <div className="mt-[10px] flex flex-col">
                <button
                  type="submit"
                  className="border p-2 w-24 rounded-md border-gray-800 hover:text-black flex cursor-pointer items-center gap-2 text-gray-600 hover:font-medium"
                  disabled={
                    form.formState.isSubmitting ||
                    !form.formState.isDirty
                  }
                >
                  Create
                </button>
              </div>
            </form>
            <Dialog.Close asChild>
              <button
                type="button"
                className="text-violet11 hover:bg-violet4 focus:shadow-violet7 absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
                aria-label="Close"
              >
                <MdClose />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};
